// Strumień SSE bramy (`GET /brama/zdarzenia`, worker-go/brama/sse.go) przez brama/strumien.ts (jak EventSource, ale
// z żetonem aplikacji ze sklepu w nagłówku). Zerwane połączenie wznawia sam z Last-Event-ID; brama odpowiada wtedy
// pełnym stanem (`urzadzenia`). Odmowa (np. 401) zamyka strumień na stałe - wtedy pytamy API, czy sesja żyje: nie -
// wylogowanie, tak - ponowienie z odstępem.
import type { AccountEvent, ListEvent, StreamEvent, ViewEvent } from "../gen/kontrakt";
import { konto } from "../api/konto";
import { przyjmijZdarzenieKonta } from "../api/pulpity";
import { przyjmijZdarzenieSerwisu } from "../api/serwis";
import { przyjmijZdarzenieUdostepnien } from "../api/udostepnienia";
import { przyjmijZdarzenieOczekujacych } from "../api/oczekujace";
import { przyjmijZdarzeniePrzypisan } from "../api/przypisanie";
import { przyjmijZdarzeniePlanow } from "../api/plany";
import { przyjmijZdarzenieRMA } from "../api/rma";
import { przyjmijZdarzenieCzatu } from "../api/czat";
import { BladZaplecza, ustawPrzyBrakuSesji } from "../api/zaplecze";
import { przyjmijWynik } from "./komendy";
import { przyjmijZdarzenie } from "./zdarzenia";
import { ustawListe, ustawPolaczenie, ustawWidok } from "./magazyn";
import { zacznijMigawke, zakonczMigawke } from "./migawka";
import { Strumien } from "./strumien";

const ODSTEPY_MS = [2000, 5000, 15000, 30000, 60000];

let zrodlo: Strumien | null = null;
let ponowienie = 0;
let proba = 0;
let poWylogowaniu: (wygasla: boolean) => void = () => {};

/** Admin „wszedł do klienta” (docs/pwa/SERWIS.md): strumień tylko jego sztuk (brama - admin dowolne numery). Tylko
 *  w pamięci - przeładowanie APP wraca do własnych urządzeń (stara APP trzymała to w localStorage bez końca). */
export interface KlientAdmina {
  nazwa: string;
  sns: readonly string[];
}
let klientAdmina: KlientAdmina | null = null;
const sluchaczeKlienta = new Set<() => void>();

export function subskrybujKlienta(f: () => void): () => void {
  sluchaczeKlienta.add(f);
  return () => sluchaczeKlienta.delete(f);
}

export const biezacyKlient = (): KlientAdmina | null => klientAdmina;

/** Przełącza strumień na sztuki klienta (null - z powrotem własne). */
export function ustawKlientaAdmina(k: KlientAdmina | null): void {
  klientAdmina = k;
  for (const f of sluchaczeKlienta) f();
  zatrzymaj();
  ustawPolaczenie("laczy");
  otworz();
}

function czytaj<T>(e: Event): T | null {
  try {
    return JSON.parse((e as MessageEvent<string>).data) as T;
  } catch {
    return null;
  }
}

async function poOdmowie(): Promise<void> {
  try {
    await konto();
  } catch (b) {
    if (b instanceof BladZaplecza && b.kod === "brak_sesji") {
      zatrzymaj();
      poWylogowaniu(true);
      return;
    }
  }
  const czekaj = ODSTEPY_MS[Math.min(proba, ODSTEPY_MS.length - 1)] ?? 60000;
  proba++;
  ponowienie = window.setTimeout(otworz, czekaj);
}

function otworz(): void {
  ponowienie = 0;
  const sns = klientAdmina?.sns.join(",");
  const es = new Strumien("/brama/zdarzenia" + (sns ? "?sn=" + encodeURIComponent(sns) : ""));
  zrodlo = es;
  es.addEventListener("open", () => {
    proba = 0;
    ustawPolaczenie("jest");
  });
  es.addEventListener("urzadzenia", (e) => {
    const m = czytaj<ListEvent>(e);
    if (m) ustawListe(m.urzadzenia);
  });
  es.addEventListener("widok", (e) => {
    const m = czytaj<ViewEvent>(e);
    if (m) ustawWidok(m.sn, m.widok);
  });
  es.addEventListener("wynik", (e) => {
    const m = czytaj<StreamEvent>(e);
    if (m) przyjmijWynik(m);
  });
  es.addEventListener("zdarzenie", (e) => {
    const m = czytaj<StreamEvent>(e);
    if (m) przyjmijZdarzenie(m);
  });
  es.addEventListener("konto", (e) => {
    const m = czytaj<AccountEvent>(e);
    if (!m) return;
    przyjmijZdarzenieKonta(m);
    przyjmijZdarzenieUdostepnien(m);
    przyjmijZdarzenieSerwisu(m);
    przyjmijZdarzenieOczekujacych(m);
    przyjmijZdarzeniePrzypisan(m);
    przyjmijZdarzeniePlanow(m);
    przyjmijZdarzenieRMA(m);
    przyjmijZdarzenieCzatu(m);
  });
  es.addEventListener("wyloguj", () => {
    zatrzymaj();
    poWylogowaniu(true);
  });
  // Lista sztuk konta sprawdzana jest przy każdym połączeniu - nowe połączenie dostaje aktualną.
  es.addEventListener("brak_dostepu", () => {
    es.close();
    void poOdmowie();
  });
  es.addEventListener("error", () => {
    ustawPolaczenie("przerwa");
    if (es.readyState === Strumien.CLOSED && zrodlo === es) void poOdmowie();
  });
}

/** Otwiera strumień (po zalogowaniu). `wylogowano(true)` - sesja skończyła się gdzie indziej albo wygasła (też odmowa
 *  sesji w zwykłym zapytaniu API - zaplecze.ustawPrzyBrakuSesji). */
export function uruchom(wylogowano: (wygasla: boolean) => void): void {
  zatrzymaj();
  klientAdmina = null;
  poWylogowaniu = wylogowano;
  ustawPrzyBrakuSesji(() => { zatrzymaj(); wylogowano(true); });
  aktywne = true;
  ustawPolaczenie("laczy");
  zacznijMigawke(); // aplikacja ze sklepu: ostatni stan urządzeń bez internetu
  otworz();
}

export function zatrzymaj(): void {
  aktywne = false;
  zakonczMigawke();
  zamknij();
}

let aktywne = false; // strumień ma działać (zalogowane konto)

function zamknij(): void {
  if (ponowienie) window.clearTimeout(ponowienie);
  ponowienie = 0;
  proba = 0;
  zrodlo?.close();
  zrodlo = null;
}

/** „Spróbuj” na pasku bez połączenia i powrót sieci w telefonie: strumień od nowa, od razu (POROWNANIE.md 26.3). */
export function polaczPonownie(): void {
  if (!aktywne) return;
  zamknij();
  ustawPolaczenie("laczy");
  otworz();
}

// Telefon zgłasza brak sieci, zanim strumień zauważy zerwanie - „Na żywo” gaśnie od razu, nie po kilkudziesięciu
// sekundach (POROWNANIE.md 26.4); po powrocie sieci strumień wraca bez czekania na odstęp ponowienia.
addEventListener("offline", () => {
  if (!aktywne) return;
  zamknij();
  ustawPolaczenie("przerwa");
});
addEventListener("online", polaczPonownie);

// Powrót z tła (zminimalizowana aplikacja, wygaszony ekran): telefon zwykle zrywa wtedy połączenie bez śladu - strumień
// od razu od nowa (brama odsyła pełny stan), nie po ciszy strażnika w strumien.ts.
const TLO_MS = 5000;
let ukryto = 0;
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") {
    ukryto = Date.now();
    return;
  }
  if (ukryto && Date.now() - ukryto > TLO_MS) polaczPonownie();
  ukryto = 0;
});
